import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ProfilePage: React.FC = () => {
  const { uniqueUrl } = useParams<{ uniqueUrl: string }>();
  const [employee, setEmployee] = useState<any>(null);
  const [companySettings, setCompanySettings] = useState<any>(null);
  const [qrCode, setQrCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canShareFiles, setCanShareFiles] = useState(false);

  useEffect(() => {
    if (uniqueUrl) {
      fetchProfile();
    }
  }, [uniqueUrl]);

  // Update favicon with company logo when settings are loaded
  useEffect(() => {
    if (companySettings?.logo) {
      const existingFavicon = document.querySelector("link[rel*='icon']");
      if (existingFavicon) {
        existingFavicon.remove();
      }

      const link = document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'icon';
      link.href = companySettings.logo;
      document.head.appendChild(link);
    }
  }, [companySettings]);

  // Check if browser supports file sharing
  useEffect(() => {
    checkFileShareSupport();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await publicAPI.getProfile(uniqueUrl!);
      setEmployee(response.employee);
      setCompanySettings(response.companySettings);
      setQrCode(response.qrCode);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Profile not found');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadVCard = async () => {
    try {
      const blob = await publicAPI.downloadVCard(uniqueUrl!);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${employee.name.replace(/\s+/g, '_')}.vcf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading vCard:', error);
    }
  };

  // Check if browser supports file sharing
  const checkFileShareSupport = async () => {
    if (typeof navigator.share === 'undefined' || typeof navigator.canShare === 'undefined') {
      setCanShareFiles(false);
      return;
    }

    try {
      // Create a test file to check if file sharing is supported
      const testFile = new File(['test'], 'test.txt', { type: 'text/plain' });
      const canShare = navigator.canShare({ files: [testFile] });
      setCanShareFiles(canShare);
    } catch (error) {
      setCanShareFiles(false);
    }
  };

  // Get vCard blob
  const getVCardBlob = async (): Promise<Blob> => {
    return await publicAPI.downloadVCard(uniqueUrl!);
  };

  // Get vCard filename
  const getVCardFileName = (): string => {
    return `${employee.name.replace(/\s+/g, '_')}.vcf`;
  };

  // Share vCard file
  const handleShareVCardFile = async (platform: string) => {
    try {
      const blob = await getVCardBlob();
      const fileName = getVCardFileName();
      const file = new File([blob], fileName, { type: 'text/vcard' });

      // Track the share event
      handleTrackEvent(`share_${platform}`);

      switch (platform) {
        case 'email':
          // Try file sharing first, fallback to URL
          if (canShareFiles) {
            try {
              await navigator.share({
                files: [file],
                title: `${employee.name}'s Contact Card`,
                text: `Contact information for ${employee.name}`
              });
            } catch (error) {
              // Fallback to mailto with URL
              const profileUrl = `${window.location.origin}/card/${uniqueUrl}`;
              window.location.href = `mailto:?subject=${encodeURIComponent(`${employee.name}'s Contact Card`)}&body=${encodeURIComponent(`Check out ${employee.name}'s contact card: ${profileUrl}`)}`;
            }
          } else {
            // Fallback to mailto with URL
            const profileUrl = `${window.location.origin}/card/${uniqueUrl}`;
            window.location.href = `mailto:?subject=${encodeURIComponent(`${employee.name}'s Contact Card`)}&body=${encodeURIComponent(`Check out ${employee.name}'s contact card: ${profileUrl}`)}`;
          }
          break;

        case 'native':
          if (canShareFiles) {
            await navigator.share({
              files: [file],
              title: `${employee.name}'s Contact Card`,
              text: `Contact information for ${employee.name}`
            });
          } else {
            // Fallback to URL sharing
            const profileUrl = `${window.location.origin}/card/${uniqueUrl}`;
            await navigator.share({
              title: `${employee.name}'s Contact Card`,
              text: `Check out my contact card`,
              url: profileUrl
            });
          }
          break;
      }
    } catch (error) {
      console.error(`Error sharing vCard via ${platform}:`, error);
    }
  };

  const handleTrackEvent = (eventType: string) => {
    publicAPI.trackEvent(employee._id, eventType).catch((err) => console.error('Tracking error:', err));
  };

  const handleShare = async (platform: string) => {
    // For email and native share, use vCard file sharing
    if (platform === 'email' || platform === 'native') {
      await handleShareVCardFile(platform);
      return;
    }

    // For WhatsApp, use URL sharing (optimal for WhatsApp)
    const profileUrl = `${window.location.origin}/card/${uniqueUrl}`;
    const shareMessage = `Check out ${employee.name}'s contact card: ${profileUrl}`;

    // Track the share event
    handleTrackEvent(`share_${platform}`);

    try {
      if (platform === 'whatsapp') {
        window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, '_blank');
      }
    } catch (error) {
      console.error(`Error sharing via ${platform}:`, error);
    }
  };

  // Helper function to darken a hex color for hover effects
  const darkenColor = (hex: string, percent: number = 10): string => {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, (num >> 16) - Math.round(255 * (percent / 100)));
    const g = Math.max(0, ((num >> 8) & 0x00FF) - Math.round(255 * (percent / 100)));
    const b = Math.max(0, (num & 0x0000FF) - Math.round(255 * (percent / 100)));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  };

  const primaryColor = companySettings?.primaryColor || '#0066ff';
  const primaryColorDark = darkenColor(primaryColor, 15);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{
      background: `linear-gradient(to bottom right, ${primaryColor}15, ${primaryColor}05)`
    }}>
      <div className="max-w-md mx-auto">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header with photo */}
          <div className="p-8 text-center" style={{
            background: `linear-gradient(to right, ${primaryColor}, ${primaryColorDark})`
          }}>
            {employee.photo ? (
              <img
                src={employee.photo}
                alt={employee.name}
                className="w-32 h-32 rounded-full mx-auto border-4 border-white shadow-lg object-cover"
              />
            ) : (
              <div className="w-32 h-32 rounded-full mx-auto border-4 border-white shadow-lg bg-white flex items-center justify-center text-4xl">
                👤
              </div>
            )}
          </div>

          {/* Contact Info */}
          <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-1">{employee.name}</h1>
            <p className="font-medium text-center mb-1" style={{ color: primaryColor }}>{employee.jobTitle}</p>
            <p className="text-gray-600 text-center text-sm mb-6">{companySettings?.companyName}</p>

            {/* Save Contact Button */}
            <button
              onClick={handleDownloadVCard}
              className="w-full text-white py-3 px-6 rounded-lg font-medium transition-colors shadow-md mb-6"
              style={{ backgroundColor: primaryColor }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = primaryColorDark}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = primaryColor}
            >
              📥 Save Contact
            </button>

            {/* Contact Actions */}
            <div className="space-y-3">
              <a
                href={`tel:${employee.phone}`}
                onClick={() => handleTrackEvent('phone_click')}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">📞</span>
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-gray-900 font-medium">{employee.phone}</p>
                  </div>
                </div>
                <span className="text-gray-400">→</span>
              </a>

              <a
                href={`mailto:${employee.email}`}
                onClick={() => handleTrackEvent('email_click')}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">✉️</span>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-gray-900 font-medium">{employee.email}</p>
                  </div>
                </div>
                <span className="text-gray-400">→</span>
              </a>

              {employee.whatsapp && (
                <a
                  href={`https://wa.me/${employee.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleTrackEvent('whatsapp_click')}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">💬</span>
                    <div>
                      <p className="text-xs text-gray-500">WhatsApp</p>
                      <p className="text-gray-900 font-medium">{employee.whatsapp}</p>
                    </div>
                  </div>
                  <span className="text-gray-400">→</span>
                </a>
              )}

              {employee.linkedin && (
                <a
                  href={employee.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleTrackEvent('linkedin_click')}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">💼</span>
                    <div>
                      <p className="text-xs text-gray-500">LinkedIn</p>
                      <p className="text-gray-900 font-medium">View Profile</p>
                    </div>
                  </div>
                  <span className="text-gray-400">→</span>
                </a>
              )}

              <a
                href={companySettings?.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleTrackEvent('website_click')}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">🌐</span>
                  <div>
                    <p className="text-xs text-gray-500">Website</p>
                    <p className="text-gray-900 font-medium">{companySettings?.companyName}</p>
                  </div>
                </div>
                <span className="text-gray-400">→</span>
              </a>
            </div>

            {/* QR Code */}
            {qrCode && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-center text-sm text-gray-600 mb-3">Scan to save contact</p>
                <div className="flex justify-center mb-4">
                  <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                </div>

                {/* Share Buttons */}
                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center gap-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                    title="Share profile URL via WhatsApp"
                  >
                    💬 WhatsApp
                  </button>
                  <button
                    onClick={() => handleShare('email')}
                    className="flex items-center gap-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                    title={canShareFiles ? "Share vCard file via email" : "Share profile URL via email"}
                  >
                    ✉️ Email
                  </button>
                  {typeof navigator.share !== 'undefined' && (
                    <button
                      onClick={() => handleShare('native')}
                      className="flex items-center gap-1 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-sm"
                      title={canShareFiles ? "Share vCard file" : "Share profile URL"}
                    >
                      ⬆️ Share
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-600">
          <p>{companySettings?.companyName}</p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
